import * as React from "react"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import {
  getStatus,
  TONE_BADGE_CLASS,
  TONE_DOT_CLASS,
  type StatusDomain,
  type StatusTone,
} from "@/lib/status-config"

/**
 * StatusBadge — the one way to render a status chip.
 *
 * Preferred (registry) API — label, tone, and icon come from the central
 * status registry (src/lib/status-config.ts), so every screen renders the
 * same status identically:
 *
 *   <StatusBadge domain="job" value={job.status} />
 *   <StatusBadge domain="interview" value={room.status} showIcon />
 *
 * Legacy (tone) API — explicit tone + children, kept for existing callers:
 *
 *   <StatusBadge status="active">Active</StatusBadge>
 */

// Legacy tone aliases retained for the pre-registry API.
const statusColors = {
  ...TONE_BADGE_CLASS,
  paused: TONE_BADGE_CLASS.pending,
  success: TONE_BADGE_CLASS.active,
  closed: TONE_BADGE_CLASS.draft,
} as const

export type StatusType = keyof typeof statusColors

export interface StatusBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Legacy API: explicit tone. Ignored when `domain` is provided. */
  status?: StatusType
  /** Registry API: the status domain (job, interview, approval, …). */
  domain?: StatusDomain
  /** Registry API: the raw status value from the backend. */
  value?: string | null
  /** Registry API: render the registry icon before the label. */
  showIcon?: boolean
  /** Overrides the registry label when provided. */
  children?: React.ReactNode
}

function StatusBadge({
  status,
  domain,
  value,
  showIcon = false,
  children,
  className,
  ...props
}: StatusBadgeProps) {
  let toneClass: string
  let content: React.ReactNode = children
  let Icon: React.ComponentType<{ className?: string }> | undefined

  if (domain) {
    const config = getStatus(domain, value)
    toneClass = TONE_BADGE_CLASS[config.tone]
    content = children ?? config.label
    Icon = showIcon ? config.icon : undefined
  } else {
    toneClass = statusColors[status ?? "draft"]
  }

  return (
    <Badge
      variant="outline"
      className={cn("border-0 gap-1", toneClass, className)}
      {...props}
    >
      {Icon ? <Icon className="h-3 w-3" aria-hidden="true" /> : null}
      {content}
    </Badge>
  )
}

/**
 * StatusDot — compact solid dot for a tone (stage-category pickers, list
 * rows). Replaces the duplicated CategoryDot implementations.
 */
export interface StatusDotProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone: StatusTone
  /** Diameter utility, e.g. "w-2 h-2" (default) or "w-1.5 h-1.5". */
  sizeClassName?: string
}

function StatusDot({ tone, sizeClassName = "w-2 h-2", className, ...props }: StatusDotProps) {
  return (
    <span
      aria-hidden
      className={cn("inline-block rounded-full shrink-0", sizeClassName, TONE_DOT_CLASS[tone], className)}
      {...props}
    />
  )
}

export { StatusBadge, StatusDot, statusColors }
